import React, { useState, useCallback, useMemo, useContext, useEffect } from "react";
import { Col, Container, Row, Card, CardBody, CardHeader } from "reactstrap";
import BreadCrumb from "../../../Components/Common/BreadCrumb/BreadCrumb";
import TableContainerMin from "../../../Components/Common/TableContainerMin";
import { useQuery, useQueryClient } from "react-query";
import { DeleteOutlined, EditOutlined,EyeOutlined,PlusCircleOutlined } from "@ant-design/icons";
import { Button, Modal, Spin } from "antd";
import HistoryContext from "../../../Components/Hooks/HistoryContext";
import toast from "react-hot-toast";
import "../../Styles/style.css";
import { deleteCatalog, getCatalogs } from "../services/catalogueApi";
import AddEditCatalogForm from "../component/AddEditCatalog";
import DetailCatalog from "../component/DetailCatalog";

const Catalogs = () => {
  const history = useContext(HistoryContext);
  const queryClient = useQueryClient();
  useEffect(() => {
    document.title = `Catalogues | ${process.env.REACT_APP_NAME} - ${process.env.REACT_APP_DESCRIPTION}`;
  }, []);
  
  const [isAddEditModalOpen, setIsAddEditModalOpen] = useState(false);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [isAffectModalOpen, setIsAffectModalOpen] = useState(false);
  const [selectedCatalog, setSelectedCatalog] = useState(null);
  const [deleteConfirmModal, setDeleteConfirmModal] = useState(false);
  const [deleteTargetId, setDeleteTargetId] = useState(null);
  const [deleteTargetRegistrationNumber, setDeleteTargetRegistrationNumber] = useState("");

  const { isLoading, isError, error, data: catalogs } = useQuery("catalogs", getCatalogs);

  const toggleAddEditModal = useCallback((catalog = null) => {
    setSelectedCatalog(catalog);
    setIsAddEditModalOpen(prev => !prev);
    if (isDetailModalOpen) setIsDetailModalOpen(false);
    if (isAffectModalOpen) setIsAffectModalOpen(false);
  }, [isDetailModalOpen, isAffectModalOpen]);

  const handleEdit = (catalog) => {
    toggleAddEditModal(catalog);
  };

  const toggleDetailModal = useCallback((catalog = null) => {
    setSelectedCatalog(catalog);
    setIsDetailModalOpen(prev => !prev);
    if (isAddEditModalOpen) setIsAddEditModalOpen(false);
    if (isAffectModalOpen) setIsAffectModalOpen(false);
  }, [isAddEditModalOpen, isAffectModalOpen]);

  const handleDetail = (catalog) => {
    toggleDetailModal(catalog);
  };

  const toggleAffectModal = useCallback((catalog = null) => {
    setSelectedCatalog(catalog);
    setIsAffectModalOpen(prev => !prev);
    if (isAddEditModalOpen) setIsAddEditModalOpen(false);
    if (isDetailModalOpen) setIsDetailModalOpen(false);
  }, [isAddEditModalOpen, isDetailModalOpen]);

  const handleAffect = (catalog) => {
    toggleAffectModal(catalog);
  };

  const handleDelete = async (catalogId) => {
    try {
      await deleteCatalog(catalogId);
      queryClient.invalidateQueries("catalogs");
      toast.success("Le catalogue a été supprimé avec succès 🎉");
    } catch (error) {
      console.error("Error deleting catalog:", error);
      toast.error(`😔 Erreur lors de la suppression du catalogue : ${error?.message || error}`);
    } finally {
      setDeleteConfirmModal(false);
    }
  };

  const openDeleteConfirmModal = (catalogId) => {
    const catalogToDelete = catalogs.find(catalog => catalog.id === catalogId);
    if (catalogToDelete) {
      setDeleteTargetId(catalogId);
      setDeleteTargetRegistrationNumber(catalogToDelete.registrationNumber);
      setDeleteConfirmModal(true);
    }
  };

  const closeDeleteConfirmModal = () => {
    setDeleteConfirmModal(false);
  };

  const columns = useMemo(() => [
    { Header: "Nom", accessor: "name", Cell: ({ row }) => ( <div>{row.original.name || "-"}</div>),filterable: false },
    { Header: "Catégory", accessor: "category",Cell: ({ row }) => ( <div>{row.original.category || "-"}</div>), filterable: false },
    { Header: "Type", accessor: "type", Cell: ({ row }) => ( <div>{row.original.type || "-"}</div>), filterable: false },
    {
      Header: "Actions",
      Cell: ({ row }) => (
        <div className="d-flex gap-2">
          <Button title="Détail d'un catalogue" size="sm" onClick={() => handleDetail(row.original)} className="btn btn-soft-info">
            <EyeOutlined />
          </Button>
          <Button title="Modifier le catalogue" size="sm" onClick={() => handleEdit(row.original)} className="btn btn-soft-warning">
            <EditOutlined />
          </Button>
          <Button title="Supprimer le catalogue" size="sm" onClick={() => openDeleteConfirmModal(row.original.id)} className="btn btn-soft-danger">
            <DeleteOutlined />
          </Button>
        </div>
      ),
      filterable: false,
    },
  ], [handleEdit, openDeleteConfirmModal, handleDetail, handleAffect]);



  const renderTabs = () => (
    <div className="d-flex gap-3 mb-3">
      {tabs.map((tab) => (
        <Button key={tab} onClick={() => handleTabChange(tab)} className={`btn ${activeTab == tab ? 'active-tab' : 'btn-light'}`}>
          {tab}
        </Button>
      ))}
    </div>
  );

  const HeaderTable = () => (
    <div className="d-flex justify-content-between align-items-center w-100">
      <div className="d-flex gap-3">{renderTabs()}</div>
      <Button type="button" className="active-tab" icon={<PlusCircleOutlined />} title="Véhicules" onClick={() => toggleAddEditModal(null)}>
        {" "} Ajouter un catalogue
      </Button>
    </div>
  );

  const tabs = ['Tous', 'Véhicule', 'Inspection ciblée', 'Inspection complète', 'Utilisateurs'];
  const [activeTab, setActiveTab] = useState('Tous');

  const handleTabChange = (tab) => {
    setActiveTab(tab);
  };
  const filteredCatalogs = useMemo(() => {
    if (!catalogs) return error?.message || [];
    switch (activeTab) {
      case 'Utilisateurs':
        return catalogs.filter(v => v.category === 'Utilisateur');
      case 'Véhicule':
        return catalogs.filter(v => v.category === 'Véhicule');
      case 'Inspection ciblée':
        return catalogs.filter(v => v.category === 'inspectionCible');
      case 'Inspection complète':
        return catalogs.filter(v => v.category === 'inspectionComplete');
      default:
        return catalogs;
    }
  }, [catalogs, activeTab]);

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb page="Catalogues" history={history} />
          <Row>
            <Col>
              <Card id="contactList">
                <CardHeader>
                  <HeaderTable />
                </CardHeader>
                <CardBody className="pt-0">
                  <TableContainerMin
                    columns={columns}
                    data={filteredCatalogs}
                    isGlobalSearch={true}
                    isGlobalFilter={true}
                    customPageSize={5}
                    className="custom-header-css"
                    divClass="table-responsive table-card mb-3"
                    tableClass="align-middle table-nowrap"
                    theadClass="table-light"
                    HeaderTable={HeaderTable}
                  />
                  {isLoading && !isError && (
                    <div className="text-center">
                      Chargement, Merci de patienter ...  <Spin />
                    </div>
                  )}
                  {filteredCatalogs && filteredCatalogs.length === 0 && !isLoading && (
                    <div className="text-center">Aucun véhicule à afficher pour le moment.</div>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>

          <AddEditCatalogForm
            modal={isAddEditModalOpen}
            setModal={setIsAddEditModalOpen}
            toggle={toggleAddEditModal}
            selectedCatalog={selectedCatalog}
          />

          <DetailCatalog
            modal={isDetailModalOpen}
            setModal={setIsDetailModalOpen}
            toggle={toggleDetailModal}
            selectedCatalog={selectedCatalog}
          />

          <Modal
            title="Confirmer la suppression"
            visible={deleteConfirmModal}
            onCancel={closeDeleteConfirmModal}
            footer={[
              <Button key="cancel" onClick={closeDeleteConfirmModal}>
                Annuler
              </Button>,
              <Button key="delete" type="danger" onClick={() => handleDelete(deleteTargetId)}>
                Supprimer
              </Button>
            ]}
          >
            <p>Êtes-vous sûr de vouloir supprimer le statut {deleteTargetRegistrationNumber} ?</p>
          </Modal>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default Catalogs;
